import { css } from '@emotion/react'
import { useRouter } from 'next/router'
import { colors, font } from 'plume-ui'
import { useEffect, useState } from 'react'
import { useUser } from '../../../../../contexts/AuthContext'
import { isHyperLink, splitMemoContentWithHyperLink } from '@/lib/company'
import { convertToJstWithTime } from '@/lib/date'
import { MEMO_EDITING, trackEvent } from '@/lib/tracking'

const MAX_MEMO_INPUT_VALUE = 3000

type Props = {
  shortName: string
  memo?: {
    content: string
    updated: string
    lastUpdateUserId: number
    lastName: string
    firstName: string
  }
  onUpdateMemo: (companyId: string, memo: string) => void
}

export const StatusMemoSection: React.FC<Props> = ({
  shortName,
  memo,
  onUpdateMemo,
}) => {
  const currentUser = useUser()
  const [editMemo, setEditMemo] = useState<
    | {
        content: string
        updated: string
        lastUpdateUserId: number
        lastName: string
        firstName: string
      }
    | undefined
  >(memo)

  const router = useRouter()

  const [isEdit, setIsEdit] = useState(false)
  const [isWarning, setIsWarning] = useState(false)

  const alertLeave = (event: Event) => {
    event.preventDefault()
    event.returnValue = false
  }

  useEffect(() => {
    setEditMemo(memo)
  }, [shortName])

  useEffect(() => {
    if (isEdit) {
      window.removeEventListener('beforeunload', alertLeave, false)
      window.removeEventListener('popstate', alertLeave, false)
      window.addEventListener('beforeunload', alertLeave, false)
      window.addEventListener('popstate', alertLeave, false)

      router.beforePopState(() => {
        window.removeEventListener('popstate', alertLeave, false)
        if (
          confirm(
            'このページを離れますか？\n行った変更が保存されない可能性があります。'
          )
        )
          return true
        history.forward()
        router.beforePopState(() => false)
        return false
      })
    }
    return () => {
      window.removeEventListener('beforeunload', alertLeave, false)
      window.removeEventListener('popstate', alertLeave, false)
      router.beforePopState(() => true)
    }
  }, [isEdit])

  const saveMemo = async (value: string) => {
    setIsWarning(false)
    if (isInvalidContent(value)) {
      // バリデーションがエラーになっても入力されたメモをstateに保存しないといけない
      editMemo
        ? setEditMemo({
            ...editMemo,
            content: value,
          })
        : setEditMemo({
            content: value,
            updated: '',
            lastUpdateUserId: -1,
            lastName: '',
            firstName: '',
          })
      setTimeout(() => setIsEdit(true), 100)
      setIsWarning(true)
    } else {
      const [userLastName, userFirstName] = currentUser.name.split(' ')
      const memoUpdated = new Date().toISOString()
      setEditMemo({
        content: value,
        updated: memoUpdated,
        lastUpdateUserId: Number(currentUser.id),
        lastName: userLastName,
        firstName: userFirstName,
      })
      onUpdateMemo(shortName, value)
      trackEvent(currentUser, MEMO_EDITING)
    }
  }

  const isInvalidContent = (content: string) =>
    content.length > MAX_MEMO_INPUT_VALUE

  return (
    <div
      css={css`
        height: 100%;
      `}
      className="memo"
    >
      <div
        css={css`
          margin-bottom: 12px;
          height: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
        `}
      >
        <div
          css={css`
            font-weight: ${font.weight.bold};
            font-size: 12px;
            line-height: 125%;
            letter-spacing: 0.02em;
            color: ${colors.textStrong};
          `}
          className="item-name section-title"
        >
          メモ
        </div>
        <div
          className="updated"
          css={css`
            font-size: 11px;
            line-height: 150%;
            letter-spacing: 0.02em;
            color: ${colors.textLight};
          `}
        >
          {editMemo && editMemo.updated
            ? `最終編集：${convertToJstWithTime(editMemo.updated)}`
            : ''}
        </div>
        <div
          className="last-update-user-name"
          css={css`
            font-size: 12px;
            line-height: 125%;
            letter-spacing: 0.02em;
            color: ${colors.textLight};
          `}
        >
          {editMemo ? `${editMemo.lastName} ${editMemo.firstName}` : ''}
        </div>
      </div>
      <div
        css={css`
          margin: auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: calc(100% - 28px);
          position: relative;
        `}
        className="text"
      >
        {isEdit ? (
          <textarea
            defaultValue={editMemo ? editMemo.content : undefined}
            autoFocus
            css={css`
              resize: none;
              width: 512px;
              height: 100%;
              padding: 16px;
              font-weight: ${font.weight.normal};
              font-size: 14px;
              line-height: 150%;
              letter-spacing: 0.02em;
              bottom: calc(100vh - 24px);
              &:focus {
                ${isWarning &&
                `outline: 1px ${colors.red60} solid;
                  border: none;`}
              }
            `}
            onFocus={(e) => e.currentTarget.setSelectionRange(3000, 3000)}
            onBlur={async (event) => {
              await saveMemo(event.target.value)
              setIsEdit(false)
            }}
          />
        ) : (
          <div
            css={css`
              position: absolute;
              overflow-y: auto;
              width: 512px;
              height: 100%;
              padding: 16px;
              border: 1px solid ${colors.gray50};
              font-weight: ${font.weight.normal};
              font-size: 14px;
              line-height: 150%;
              letter-spacing: 0.02em;
              background-color: ${colors.white00};
              white-space: pre-wrap;
              overflow-wrap: break-word;
              cursor: text;
            `}
            onClick={() => setIsEdit(true)}
          >
            {splitMemoContentWithHyperLink(editMemo).map(
              (contentPart, index) => {
                return isHyperLink(contentPart) ? (
                  <a
                    href={contentPart}
                    target="_blank"
                    css={css`
                      color: ${colors.link};
                    `}
                    key={index}
                  >
                    {contentPart}
                  </a>
                ) : (
                  contentPart
                )
              }
            )}
          </div>
        )}
      </div>
      {isWarning && (
        <div
          css={css`
            font-size: 12px;
            line-height: 125%;
            letter-spacing: 0.02em;
            color: ${colors.red60};
            padding-top: 8px;
            padding-bottom: 24px;
          `}
          className="warning-message"
        >
          保存できる最大文字数を超えています
        </div>
      )}
    </div>
  )
}
